import React, { useState, useEffect, useRef } from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';

declare global {
  interface Window {
    TradingView?: any;
  }
}

const PriceChart: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('15');
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  
  const timeframes = [
    { label: '1분', value: '1' },
    { label: '5분', value: '5' },
    { label: '15분', value: '15' },
    { label: '1시간', value: '60' },
    { label: '1일', value: '1D' }
  ];

  useEffect(() => {
    if (chartContainerRef.current) {
      // 기존 내용 제거
      chartContainerRef.current.innerHTML = '';
      
      // TradingView 위젯 div 생성
      const widgetDiv = document.createElement('div');
      widgetDiv.className = 'tradingview-widget-container';
      widgetDiv.style.height = '100%';
      widgetDiv.style.width = '100%';
      widgetDiv.style.position = 'relative';
      
      const widgetInner = document.createElement('div');
      widgetInner.className = 'tradingview-widget-container__widget';
      widgetInner.style.height = '100%';
      widgetInner.style.width = '100%';
      widgetInner.style.position = 'absolute';
      widgetInner.style.top = '0';
      widgetInner.style.left = '0';
      
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
      script.async = true;
      
      // 테마에 따른 색상 설정
      const isDark = theme === 'dark';
      const config = {
        "width": "100%",
        "height": "100%",
        "autosize": true,
        "symbol": "BINANCE:BTCUSDT",
        "interval": selectedTimeframe,
        "timezone": "Asia/Seoul",
        "theme": isDark ? "dark" : "light",
        "style": "1",
        "locale": "kr",
        "enable_publishing": false,
        "backgroundColor": isDark ? "rgba(19, 23, 34, 1)" : "rgba(255, 255, 255, 1)",
        "gridColor": isDark ? "rgba(42, 46, 57, 1)" : "rgba(229, 231, 235, 1)",
        "hide_top_toolbar": false,
        "hide_legend": false,
        "save_image": false,
        "hide_side_toolbar": false,
        "allow_symbol_change": false,
        "details": false,
        "hotlist": false,
        "calendar": false,
        "withdateranges": false,
        "hide_volume": false,
        "container_id": "tradingview_widget"
      };
      
      script.innerHTML = JSON.stringify(config);
      
      widgetDiv.appendChild(widgetInner);
      widgetDiv.appendChild(script);
      
      chartContainerRef.current.appendChild(widgetDiv);
    }
  }, [selectedTimeframe, theme]); // theme 의존성 추가

  const handleTimeframeChange = (timeframe: string) => {
    setSelectedTimeframe(timeframe);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <ChartBarIcon className="h-5 w-5 mr-2" />
          💰 BTC/USDT 차트
        </h3>
        <div className="flex space-x-1">
          {timeframes.map((tf) => (
            <button
              key={tf.value}
              onClick={() => handleTimeframeChange(tf.value)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                selectedTimeframe === tf.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-light-200 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-light-300 dark:hover:bg-dark-600'
              }`}
            >
              {tf.label}
            </button>
          ))}
        </div>
      </div>
      
      <div className="flex-1 bg-light-100 dark:bg-dark-900 rounded-lg overflow-hidden border border-light-300 dark:border-dark-700 flex flex-col" style={{ minHeight: '250px' }}>
        <div
          ref={chartContainerRef}
          className="flex-1"
          style={{ 
            height: '100%', 
            width: '100%',
            position: 'relative',
            minHeight: '200px'
          }}
        >
          {/* 로딩 상태 */}
          <div className="flex items-center justify-center h-full bg-white dark:bg-dark-800 rounded-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <div className="text-gray-600 dark:text-gray-400">TradingView 차트 로딩 중...</div>
              <div className="text-xs text-gray-500 dark:text-gray-500 mt-2">실시간 BTC/USDT 데이터</div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-3 grid grid-cols-4 gap-3 flex-shrink-0">
        <div className="bg-light-100 dark:bg-dark-700 rounded p-2 text-center border border-light-300 dark:border-dark-600">
          <div className="text-xs text-gray-600 dark:text-gray-400">Open</div>
          <div className="text-gray-900 dark:text-white font-medium">$43,850</div>
        </div>
        <div className="bg-light-100 dark:bg-dark-700 rounded p-2 text-center border border-light-300 dark:border-dark-600">
          <div className="text-xs text-gray-600 dark:text-gray-400">High</div>
          <div className="text-green-600 dark:text-green-400 font-medium">$44,890</div>
        </div>
        <div className="bg-light-100 dark:bg-dark-700 rounded p-2 text-center border border-light-300 dark:border-dark-600">
          <div className="text-xs text-gray-600 dark:text-gray-400">Low</div>
          <div className="text-red-600 dark:text-red-400 font-medium">$43,200</div>
        </div>
        <div className="bg-light-100 dark:bg-dark-700 rounded p-2 text-center border border-light-300 dark:border-dark-600">
          <div className="text-xs text-gray-600 dark:text-gray-400">Volume</div>
          <div className="text-blue-600 dark:text-blue-400 font-medium">28.5M</div>
        </div>
      </div>
    </div>
  );
};

export default PriceChart; 